import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { WeatherSlide } from 'src/content/screens/slide-element';
import 'src/library/editors/elements/d-select-dropdown';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import countiesAndMunicipals from 'src/store/data/counties-and-municipals.json';

interface Municipal {
  name: string;
  code: string;
  latitude: number;
  longitude: number;
}

import { LocalDate } from 'src/utilities/local-date';
import { styleMap } from 'lit/directives/style-map.js';
import { weatherSymbolKeys } from 'src/content/screens/weather-symbol-keys';
import { capitalize } from 'src/utilities/text';
import type { DailyWeatherSummary } from 'src/content/screens/weather';
import { classMap } from 'lit/directives/class-map.js';
import { t } from 'src/i18n';

// Static data — flatten once at module load instead of on every render.
const ALL_MUNICIPALS: Municipal[] = countiesAndMunicipals.flatMap((c) => c.municipals);
const COUNTY_OPTIONS: SelectDropdownOption[] = countiesAndMunicipals.map((c) => ({ value: c.code, text: c.name }));
const FALLBACK_SYMBOL = 'clearsky_day';

// Per-kommune in-memory cache så flere parallelle weather-slides eller
// gjentatte mounts ikke trigger duplikat-fetches mot backend. Backend
// refresher snapshots ca. én gang per halvtime via WeatherFeedRefreshJob,
// så 30 min klient-TTL gir bounded staleness uten unødvendige nettverkskall.
const FORECAST_CACHE_TTL_MS = 30 * 60 * 1000;
const forecastCache = new Map<string, { forecast: DailyWeatherSummary[]; fetchedAt: number }>();

interface WeatherFeedSnapshot {
  updatedAt?: string;
  forecast?: DailyWeatherSummary[];
}

/**
 * Henter værvarsel fra backend-snapshot per kommune (`/feeds/weather/{kode}.json`).
 * Backend lager snapshot lazy ved første treff og oppdaterer via scheduled job —
 * se trinnvis/dabih-api `MetWeatherIngestor` + `WeatherFeedRefreshJob`.
 *
 * Forecast er dynamisk og lagres ikke i slide-payloaden (samme mønster som
 * `d-news-slide` som får `feed`-prop separat). Live-data hentes inn til lokal
 * `liveForecast` ved mount + når kommune endres, og rendres direkte. Backend
 * er sannhetskilde også for player-siden, så vi trenger ikke å persistere
 * forecast.
 */
@customElement('d-weather-slide')
export class DWeatherSlide extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      position: relative;
      aspect-ratio: 16/9;
      container-type: inline-size;
      background: white;
    }
    .wrapper {
      padding: 4cqi 4cqi 2cqi 4cqi;
      background: url(/images/yr.svg) right 3.5cqw top 3cqw no-repeat;
      background-size: 7cqw auto;
    }
    .title {
      height: 9cqi;
      font-family: var(--screen-slide-header-font), sans-serif;
      font-size: 3.8cqi;
      font-weight: 600;
      line-height: 120%;
    }
    .day {
      display: flex;
      justify-content: space-between;
      height: 7.5cqi;
      border-top: 0.2cqi solid var(--borderColor);
      font-family: var(--screen-slide-header-font), sans-serif;
      font-size: 3cqi;
      line-height: 120%;
      font-weight: 500;
    }
    .day:last-child {
      border-bottom: 0.2cqi solid var(--borderColor);
    }
    .day > div {
      flex: none;
      display: flex;
      align-items: center;
    }
    .date {
      width: 36cqi;
    }
    .symbol {
      width: 8cqi;
      background-position: 50% 50%;
      background-size: 7cqi auto;
      background-repeat: no-repeat;
    }
    .temp {
      width: 14cqi;
      padding-left: 8cqi;
      font-size: 3cqi;
      font-weight: 100;
      color: var(--borderColor);
    }
    .temp-value {
      width: 5cqi;
      color: #c60101;
      font-size: 4cqi;
      font-weight: 600;
    }
    .temp-value:first-child {
      border-right: 0.2cqi solid var(--borderColor);
      padding-right: 1.2cqi;
      text-align: right;
    }
    .temp-value:last-child {
      padding-left: 1cqi;
    }
    .temp-value.cold {
      color: var(--themeColor);
    }
    .wind {
      width: 14cqi;
      text-align: right;
      justify-content: flex-end;
      font-weight: 400;
    }
    .wind-value {
      color: black;
      font-size: 4cqi;
      font-weight: 600;
    }
    .loading {
      padding: 2cqi 0;
      font-family: var(--screen-slide-header-font), sans-serif;
      font-size: 2.5cqi;
      font-style: italic;
    }
    .controllers-position {
      position: absolute;
      bottom: 20px;
      right: 20px;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
    }
    .controllers {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: 12px;
      border-radius: 16px;
      box-shadow: 0 2px 10px hsla(1, 0%, 0%, 0.3);
      background: white;
      padding: 12px;
    }
  `;
  @property({ type: Object })
  slide!: WeatherSlide;
  @property({ type: Boolean })
  edit = false;
  @property({ type: String })
  assetBase = '';
  @property({ type: Array })
  sampleForecast: DailyWeatherSummary[] | null = null;
  /**
   * Den faktiske forecasten som rendres. Settes normalt internt av
   * `refreshForecast()`, men eksponert som `@property` så Storybook/tester
   * kan injisere mockdata direkte med `.liveForecast=${...}`.
   */
  @property({ attribute: false })
  liveForecast: DailyWeatherSummary[] | null = null;
  private lastFetchedKey = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this.refreshForecast();
  }

  protected updated(changedProps: Map<string, unknown>): void {
    // Trigger fetch kun når kommune eller assetBase faktisk endrer seg —
    // ikke ved hver slide-ref-endring (ellers looper vi når vi muterer
    // parent-state og parent-state muterer oss tilbake).
    if (changedProps.has('slide') || changedProps.has('assetBase')) {
      const key = `${this.assetBase}|${this.slide?.municipalCode ?? ''}`;
      if (key !== this.lastFetchedKey) {
        this.lastFetchedKey = key;
        void this.refreshForecast();
      }
    }
  }

  private get municipalOptions(): SelectDropdownOption[] {
    if (this.slide.countyCode) {
      const county = countiesAndMunicipals.find((c) => c.code === this.slide.countyCode);
      if (county) {
        return county.municipals.map((m) => ({ value: m.code, text: m.name }));
      }
    }
    return [];
  }

  /**
   * Fetch fresh forecast fra backend hvis vi har både `assetBase` og en
   * `municipalCode`. Bruker in-memory cache for å dempe duplikat-fetches.
   * Resultat lagres i `liveForecast` (lokal state) — slide-prop-en mutéres
   * IKKE, så vi unngår update-loopen mellom denne komponenten og parent.
   */
  private async refreshForecast(): Promise<void> {
    const municipalCode = this.slide?.municipalCode;
    if (!this.assetBase || !municipalCode) {
      this.liveForecast = null;
      return;
    }

    const cached = forecastCache.get(municipalCode);
    if (cached && Date.now() - cached.fetchedAt < FORECAST_CACHE_TTL_MS) {
      this.liveForecast = cached.forecast;
      return;
    }

    const url = `${this.assetBase}/feeds/weather/${municipalCode}.json?t=${Date.now()}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        // Backend lazy-creates ved første miss og burde alltid svare 200
        // etter det. Logg hvis vi ser noe annet og behold liveForecast som
        // den var (kan være null → fallback til slide.forecast i render).
        console.warn(`[d-weather-slide] forecast fetch failed: ${response.status}`);
        return;
      }
      const snapshot = (await response.json()) as WeatherFeedSnapshot;
      const forecast = (snapshot.forecast ?? []).slice(0, 5).map((d) => ({
        ...d,
        symbol: d.symbol || FALLBACK_SYMBOL,
      }));
      forecastCache.set(municipalCode, { forecast, fetchedAt: Date.now() });
      // Hopp over hvis kommunen ble byttet ut mens fetch var underveis.
      if (this.slide?.municipalCode === municipalCode) {
        this.liveForecast = forecast;
      }
    } catch (err) {
      console.warn('[d-weather-slide] forecast fetch error:', err);
    }
  }

  private onSelectCounty(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.liveForecast = null;
    this.slide = { ...this.slide, countyCode: e.detail.value, municipalCode: '' };
    this.dispatchEvent(
      new CustomEvent<{ value: WeatherSlide }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.slide },
      }),
    );
  }

  private onSelectMunicipal(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    const municipal = ALL_MUNICIPALS.find((m) => m.code === e.detail.value);
    if (!municipal) return;
    // Persistér valget umiddelbart. liveForecast oppdateres av refreshForecast
    // når updated() registrerer at municipalCode er endret.
    this.liveForecast = null;
    this.slide = {
      ...this.slide,
      municipalCode: municipal.code,
      latitude: municipal.latitude,
      longitude: municipal.longitude,
    };
    this.dispatchEvent(
      new CustomEvent<{ value: WeatherSlide }>('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.slide },
      }),
    );
  }

  private get municipalName(): string {
    const municipal = ALL_MUNICIPALS.find((m) => m.code === this.slide.municipalCode);
    if (municipal) {
      return municipal.name;
    }
    return '';
  }

  private symbolStyle(symbol: string) {
    const filename = weatherSymbolKeys[symbol] ?? weatherSymbolKeys[FALLBACK_SYMBOL];
    return { backgroundImage: `url(/images/weather-symbols/${filename}.svg)` };
  }

  private tempClass(temp: number) {
    return {
      'temp-value': true,
      cold: temp < 1,
    };
  }

  render() {
    let forecast = this.liveForecast ?? [];
    if (this.sampleForecast) {
      forecast = this.sampleForecast;
    }
    const loading = this.slide?.municipalCode && this.liveForecast === null;
    return html`
      <div class="wrapper">
        <div class="title">
          ${t('screens.weatherSlide.title', {
            place: this.municipalName ? this.municipalName : t('screens.weatherSlide.yourMunicipality'),
          })}
        </div>
        ${loading && forecast.length === 0
          ? html`<div class="loading">${t('screens.weatherSlide.loading')}</div>`
          : nothing}
        ${forecast.map((day) => {
          return html`
            <div class="day">
              <div class="date">${capitalize(LocalDate.fromString(day.date).toStringForDisplayWithDayOfWeek())}</div>
              <div class="symbol" style=${styleMap(this.symbolStyle(day.symbol))}></div>
              <div class="temp">
                <div class=${classMap(this.tempClass(day.maxTemp))}>${day.maxTemp}°</div>
                <div class=${classMap(this.tempClass(day.minTemp))}>${day.minTemp}°</div>
              </div>
              <div class="wind"><span class="wind-value">${day.maxWind}</span>&nbsp;m/s</div>
            </div>
          `;
        })}
      </div>
      ${this.edit
        ? html`
            <div class="controllers-position">
              <div class="controllers">
                <d-select-dropdown
                  controller
                  label=${t('screens.weatherSlide.county')}
                  placeholder=${t('screens.weatherSlide.selectCounty')}
                  .options=${COUNTY_OPTIONS}
                  .value=${this.slide.countyCode}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => this.onSelectCounty(e)}
                ></d-select-dropdown>
                <d-select-dropdown
                  controller
                  label=${t('screens.weatherSlide.municipality')}
                  placeholder=${t('screens.weatherSlide.selectMunicipality')}
                  .options=${this.municipalOptions}
                  .value=${this.slide.municipalCode}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => this.onSelectMunicipal(e)}
                ></d-select-dropdown>
              </div>
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-weather-slide': DWeatherSlide;
  }
}
